import React, { useEffect, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';

import { Pill, XomicsSelect } from './parts';
import { Field, fieldAria } from './ui';
import { tx } from './i18n';
import { Switch } from './ProductOptions';

// The sections under the top of a product's page, as the Shop page tab lists
// them: in the order the page shows them, each a card that opens, with a plain
// input for every heading, card, step and question. They are the product's own
// page words (storefront.site.product_<id>.<field>), so this only says which
// fields a section has and how each is typed in; the storefront's page file of
// the product's layout (public/site/products/<layout>.html) draws them, and
// leaves a section out while it has no words.

// The drawings a benefit can carry, and the ones a test's area can.
const BENEFIT_ICONS = ['shield', 'target', 'waves', 'spark', 'brain', 'leaf', 'bolt', 'drop', 'dna', 'wind'];
const AREA_ICONS = [
  'cgHelix', 'cgGenes', 'cgReport', 'cgFlask', 'cgDrop', 'cgEar', 'cgRun', 'cgFamily', 'cgCalendar', 'cgBaby',
  'cgLoop', 'cgHeart', 'cgPill', 'cgLeaf', 'cgBrain', 'cgBolt', 'cgShield', 'cgPerson', 'cgApp', 'cgGut',
  'cgFlame', 'cgScale', 'cgMicrobe', 'cgEye', 'cgSprout', 'cgTooth', 'cgWind', 'cgDumbbell', 'cgMoon',
];
// The app screens the phone beside a test's figures can show.
const PHONE_SCREENS = [
  'gx-health', 'gx-carrier', 'gx-drugs', 'gx-traits', 'gx-pdf', 'mb-gut', 'mb-oral', 'mb-diversity', 'mb-phyla',
  'mb-risk', 'mb-pdf', 'carrier-app', 'newborn-app', 'sports-app', 'consult', 'clinical', 'clinical-review',
  'insights', 'home', 'tests', 'diet', 'habits', 'sleep', 'hrv', 'hr',
];
const TABLE_COLUMNS = 3;

const numbered = (count, prefix, parts) => ({ count, prefix, parts });

// A section: the fields that put it on the page (any of `shows` with words),
// its heading (a small first line and the heading), its other fields, and the
// things it repeats (cards, steps, questions). A part is [name, kind].
const SECTIONS = {
  why: {
    shows: ['why_title', 'why_title_lead', 'why_1_title'],
    heading: 'why',
    items: numbered(3, 'why', [['title', 'line'], ['text', 'text']]),
    item: 'card',
  },
  how: {
    shows: ['how_title', 'how_title_lead', 'how_1_title', 'how_table'],
    heading: 'how',
    fields: [['how_text', 'text', 'line_under']],
    items: numbered(6, 'how', [['title', 'line'], ['text', 'text'], ['badge', 'line']]),
    item: 'step',
    table: 'how_table',
  },
  inside: {
    shows: ['inside_title', 'inside_title_lead', 'inside_1_title', 'specs'],
    heading: 'inside',
    items: numbered(3, 'inside', [['title', 'line'], ['text', 'text'], ['icon', 'benefit']]),
    item: 'benefit',
  },
  areas: {
    shows: ['area_1_title'],
    fields: [['areas_title', 'line', 'heading'], ['areas_text', 'text', 'line_under']],
    items: numbered(9, 'area', [['title', 'line'], ['sub', 'line'], ['text', 'text'], ['tags', 'list'], ['icon', 'area']]),
    item: 'area',
  },
  matters: {
    shows: ['matters_title', 'matters_title_lead', 'matters_1_value'],
    heading: 'matters',
    items: numbered(6, 'matters', [['value', 'line'], ['label', 'line'], ['text', 'text'], ['screen', 'screen']]),
    item: 'figure',
    fields: [['matters_link_title', 'line', 'link_title'], ['matters_link_text', 'line', 'link_text'],
      ['matters_link', 'line', 'link_address']],
    fieldsLast: true,
  },
  faq: {
    shows: ['faq_1_question'],
    fields: [['faq_title', 'line', 'heading']],
    items: numbered(8, 'faq', [['question', 'line'], ['answer', 'text']]),
    item: 'question',
  },
  end: {
    shows: ['end_title', 'end_title_lead', 'end_text'],
    heading: 'end',
    fields: [['end_text', 'text', 'line_under'], ['end_price_note', 'line', 'end_price_note'], ['end_note', 'line', 'end_note']],
  },
};

// The sections a layout's page has, in the page's order.
const LAYOUT_SECTIONS = {
  standard: ['why', 'how', 'inside', 'faq', 'end'],
  kit: ['areas', 'matters', 'faq', 'end'],
  wearable: [],
};
// What a layout's closing card adds.
const LAYOUT_EXTRA = { kit: { end: [['end_note_2', 'line', 'end_note_2']] } };

function fieldsOf(name, layout) {
  const section = SECTIONS[name];
  const out = [];
  if (section.heading) out.push(`${section.heading}_title_lead`, `${section.heading}_title`);
  (section.fields || []).forEach(([field]) => out.push(field));
  (LAYOUT_EXTRA[layout]?.[name] || []).forEach(([field]) => out.push(field));
  if (section.items) {
    for (let n = 1; n <= section.items.count; n += 1) {
      section.items.parts.forEach(([part]) => out.push(`${section.items.prefix}_${n}_${part}`));
    }
  }
  if (section.table) out.push(section.table);
  return out;
}

// Every field a section of any layout uses (the Shop page tab lists these
// here, not among its other fields).
export const SECTION_FIELDS = new Set([
  ...Object.keys(LAYOUT_SECTIONS).flatMap((layout) => LAYOUT_SECTIONS[layout].flatMap((name) => fieldsOf(name, layout))),
  'page_order',
]);

// A table's words as rows of cells, and back (cells parted by " | ", a row a line).
function tableRows(value) {
  const rows = String(value || '').split('\n').map((line) => line.trim()).filter(Boolean)
    .map((line) => line.split('|').map((cell) => cell.trim()));
  return rows.map((cells) => Array.from({ length: TABLE_COLUMNS }, (_, index) => cells[index] ?? ''));
}
function tableValue(rows) {
  const kept = rows.filter((cells) => cells.some((cell) => cell.trim() !== ''));
  return kept.map((cells) => cells.map((cell) => cell.trim()).join(' | ').trim()).join('\n');
}

const blankRow = () => Array(TABLE_COLUMNS).fill('');
const withBlank = (rows) => (rows.length === 0 ? [blankRow()] : rows);

// The grid keeps its own rows, so a row being typed is not lost while it is
// still empty; the field gets the rows that have words.
function TableEditor({ id, value, disabled, onChange }) {
  const [shown, setShown] = useState(() => withBlank(tableRows(value)));
  const emitted = useRef(value);
  // The words changed from outside (a discard, a reload, another language).
  useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setShown(withBlank(tableRows(value)));
  }, [value]);
  const update = (next) => {
    const text = tableValue(next);
    emitted.current = text;
    setShown(next);
    onChange(text);
  };
  const change = (row, column, text) => {
    const next = shown.map((cells) => [...cells]);
    next[row][column] = text.replace(/\|/g, '');
    update(next);
  };
  const add = () => update([...shown, blankRow()]);
  const remove = (row) => update(withBlank(shown.filter((_, index) => index !== row)));
  return <div className="xo-pm-table" role="group" aria-labelledby={`${id}-label`}>
    {shown.map((cells, row) => <div className="xo-pm-table-row" key={row}>
      <span className="xo-pm-table-n">{row === 0 ? tx('ui.products.page.section.table_head') : row}</span>
      {cells.map((cell, column) => <input key={column} type="text" dir="auto" value={cell} disabled={disabled}
        aria-label={tx('ui.products.page.section.table_cell', { row: row + 1, column: column + 1 })}
        onChange={(event) => change(row, column, event.target.value)} />)}
      <button type="button" className="xo-icon-btn" disabled={disabled || shown.length === 1}
        aria-label={tx('ui.products.page.section.table_remove', { row: row + 1 })} onClick={() => remove(row)}>
        <Minus aria-hidden="true" /></button>
    </div>)}
    <button type="button" className="xo-link" disabled={disabled} onClick={add}>
      <Plus aria-hidden="true" /> {tx('ui.products.page.section.table_add')}</button>
  </div>;
}

function Section({
  name, layout, productId, form, shown, set, input, disabled, settingsDisabled, mayEdit,
}) {
  const section = SECTIONS[name];
  const value = shown;
  const filled = section.items
    ? Array.from({ length: section.items.count }, (_, index) => index + 1)
      .filter((n) => section.items.parts.some(([part]) => value(`${section.items.prefix}_${n}_${part}`) !== '')).pop() || 0
    : 0;
  const [open, setOpen] = useState(0);
  const count = section.items ? Math.max(open, filled, 1) : 0;
  const onPage = section.shows.some((field) => value(field) !== '');
  const label = (key, variables) => tx(`ui.products.page.section.${key}`, variables);
  const id = (field) => `product-${productId}-page-${field}`;

  // A drawing or a screen is a setting, not words: it is chosen once, in English.
  const choice = (field, key, options) => (
    <Field id={id(field)} label={label(key)}>
      <XomicsSelect {...fieldAria(id(field))} value={form[field] ?? ''} disabled={settingsDisabled}
        onChange={(event) => set(field, event.target.value)}>
        <option value="">{label('choose')}</option>
        {[...new Set([...(form[field] ? [form[field]] : []), ...options])].map((option) => (
          <option key={option} value={option}>{option}</option>))}
      </XomicsSelect>
    </Field>
  );
  const part = (prefix, n, [partName, kind]) => {
    const field = `${prefix}_${n}_${partName}`;
    if (kind === 'benefit') return choice(field, 'icon', BENEFIT_ICONS);
    if (kind === 'area') return choice(field, 'icon', AREA_ICONS);
    if (kind === 'screen') return choice(field, 'screen', PHONE_SCREENS);
    return input(field, kind, label(`part_${partName}`), kind === 'list' ? label('one_per_line') : undefined);
  };
  const plain = ([field, kind, key]) => (
    <div key={field} className={kind === 'line' ? undefined : 'xo-product-field-wide'}>{input(field, kind, label(key))}</div>
  );
  const own = [...(section.fields || []), ...(LAYOUT_EXTRA[layout]?.[name] || [])];

  return <details className="xo-pm-card xo-pm-section" aria-labelledby={`product-${productId}-section-${name}`}>
    <summary>
      <span className="xo-pm-section-name">
        <strong id={`product-${productId}-section-${name}`}>{label(name)}</strong>
        <small>{label(`${name}_hint`)}</small>
      </span>
      <Pill tone={onPage ? 'ok' : 'flat'}>{onPage ? label('on') : label('off')}</Pill>
    </summary>
    <div className="xo-pm-section-body">
      {(section.heading || (own.length > 0 && !section.fieldsLast)) && <div className="xo-product-details-grid">
        {section.heading && <>
          {input(`${section.heading}_title_lead`, 'line', label('heading_lead'))}
          {input(`${section.heading}_title`, 'line', label('heading'))}
        </>}
        {!section.fieldsLast && own.map(plain)}
      </div>}
      {section.items && <>
        {Array.from({ length: count }, (_, index) => index + 1).map((n) => (
          <div key={n} className="xo-pm-page-detail">
            <h4 className="xo-pm-page-sub">{label(section.item, { number: n })}</h4>
            <div className="xo-product-details-grid">
              {section.items.parts.map((entry) => (
                <div key={entry[0]} className={entry[1] === 'text' || entry[1] === 'list' ? 'xo-product-field-wide' : undefined}>
                  {part(section.items.prefix, n, entry)}
                </div>
              ))}
            </div>
          </div>
        ))}
        {mayEdit && count < section.items.count && (
          <button type="button" className="xo-link" disabled={disabled} onClick={() => setOpen(count + 1)}>
            <Plus aria-hidden="true" /> {label(`add_${section.item}`)}</button>
        )}
      </>}
      {section.table && <div className="xo-pm-page-detail">
        <h4 className="xo-pm-page-sub" id={`${id(section.table)}-label`}>{label('table')}</h4>
        <p className="xo-pm-page-note">{label('table_hint')}</p>
        <TableEditor id={id(section.table)} value={form[section.table] ?? ''} disabled={disabled}
          onChange={(text) => set(section.table, text)} />
      </div>}
      {section.fieldsLast && own.length > 0 && <div className="xo-product-details-grid">{own.map(plain)}</div>}
    </div>
  </details>;
}

// `input(field, kind, label, hint)` is the Shop page tab's own (one input for
// one field, in the language being written); `shown(field)` the words the
// page would show for a field (this language's, or the English where it has
// none); `settingsDisabled` is true while another language than English is
// being written, where drawings, screens and the order cannot be changed.
export default function ProductPageSections({
  layout, productId, form, shown, set, input, disabled, settingsDisabled, mayEdit,
}) {
  const names = LAYOUT_SECTIONS[layout] || [];
  if (names.length === 0) return null;
  const mattersFirst = shown('page_order').startsWith('matters');
  return <section className="xo-pm-sections" aria-labelledby={`product-${productId}-sections-title`}>
    <div className="xo-pm-section-head">
      <div>
        <h3 id={`product-${productId}-sections-title`}>{tx('ui.products.page.section.title')}</h3>
        <p>{tx('ui.products.page.section.hint')}</p>
      </div>
    </div>
    {layout === 'kit' && <div className="xo-pm-page-switch">
      <span>
        <strong>{tx('ui.products.page.section.matters_first')}</strong>
        <small>{tx('ui.products.page.section.matters_first_hint')}</small>
      </span>
      <Switch on={mattersFirst} disabled={settingsDisabled} label={tx('ui.products.page.section.matters_first')}
        onChange={(value) => set('page_order', value ? 'matters areas' : '')} />
    </div>}
    {(layout === 'kit' && mattersFirst ? ['matters', 'areas', ...names.filter((name) => name !== 'matters' && name !== 'areas')] : names)
      .map((name) => <Section key={name} name={name} layout={layout} productId={productId} form={form} shown={shown}
        set={set} input={input} disabled={disabled} settingsDisabled={settingsDisabled} mayEdit={mayEdit} />)}
  </section>;
}
